import React from 'react'
import { useQuery } from '@tanstack/react-query'
import axiosClient from '@/api/core/axiosClient'
import { setShippingConfig, type ShippingGeneralConfig } from '@/lib/shippingConfig'

type StateOption = { code: string; name: string }

type AppConfig = {
  ready: boolean
  shipping: (ShippingGeneralConfig & { message?: string }) | null
  regional: { defaultCurrency: string; defaultCountry: string; defaultTimezone?: string; availableStates?: StateOption[] } | null
  payment: { enabledMethods: string[]; defaultMethod: string; gateways: any[] } | null
  integration: {
    analytics: { provider: string; trackingId: string }[];
    logistics?: { provider: string; apiKey: string; settings?: any }[];
  } | null
  config?: {
    seo?: {
      siteName?: string
      favicon?: string
      defaultTitle?: string
      titleTemplate?: string
      defaultDescription?: string
      defaultKeywords?: string[]
      defaultImage?: string
      twitterCard?: 'summary' | 'summary_large_image'
      staticPages?: Record<string, {
        title?: string
        description?: string
        keywords?: string[]
      }>
      socialMedia?: Record<string, string>
      analytics?: {
        googleTagManagerId?: string
        googleSiteVerification?: string
      }
    }
    business?: {
      name: string
      address: {
        street: string
        city: string
        state: string
        postalCode: string
        country: string
      }
      contact: {
        phone: string
        email: string
      }
    }
  }
}

const AppConfigContext = React.createContext<AppConfig>({
  ready: false,
  shipping: null,
  regional: null,
  payment: null,
  integration: null,
  config: undefined,
})

export const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data: configData, isSuccess } = useQuery({
    queryKey: ['app-config'],
    queryFn: async () => {
      return await axiosClient.get('/configuration/public') as any
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })

  const shipping: AppConfig['shipping'] = React.useMemo(() => {
    const s = configData?.shipping
    if (!s) return null
    const type = String(s.type || '').toLowerCase() as ShippingGeneralConfig['type']
    const normalized: ShippingGeneralConfig & { message?: string } = {
      enabled: (s.enabled ?? true) as boolean,
      type,
      flatRate: s.flatRate != null ? Number(s.flatRate) : undefined,
      freeShippingThreshold: s.freeShippingThreshold != null ? Number(s.freeShippingThreshold) : undefined,
      defaultCost: s.defaultCost != null ? Number(s.defaultCost) : undefined,
      message: s.message ?? undefined,
    }
    return normalized
  }, [configData])

  React.useEffect(() => {
    if (shipping) {
      setShippingConfig(shipping)
    }
  }, [shipping])

  const regional: AppConfig['regional'] = configData?.regional
    ? {
      defaultCurrency: configData.regional.defaultCurrency,
      defaultCountry: configData.regional.defaultCountry,
      availableStates: configData.regional.availableStates,
    }
    : null

  const payment: AppConfig['payment'] = configData?.payment
    ? {
      enabledMethods: configData.payment.enabledMethods || [],
      defaultMethod: configData.payment.defaultMethod || 'credit_card',
      gateways: configData.payment.gateways || [],
    }
    : null

  const integration: AppConfig['integration'] = configData?.integration
    ? {
      analytics: configData.integration.analytics || [],
      logistics: configData.integration.logistics || [],
    }
    : null

  const ready = !!shipping && !!regional && !!payment && isSuccess

  return (
    <AppConfigContext.Provider value={{ ready, shipping, regional, payment, integration, config: configData }}>
      {children}
    </AppConfigContext.Provider>
  )
}

export const useAppConfig = () => React.useContext(AppConfigContext)
